import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { HttpUtilsService, QueryParamsModel } from '../../_base/crud';
import { AppState } from '../../reducers';
import { currentAuthToken } from '../../auth/_selectors/auth.selectors';
import { environment } from '../../../../environments/environment';
import { ServiceCatalogModel } from '../_models/service-catalog.model';

const API_URL = `${environment.TIMELINE_BASE}/api/v1/service-catalogs`;

@Injectable()
export class ServiceCatalogService {
  private authToken;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
  }

  findServiceCatalogs(
    queryParams: QueryParamsModel,
  ): Observable<{ items: ServiceCatalogModel[]; totalCount: number }> {
    let params = new HttpParams()
      .set('page', `${queryParams.pageNumber}`)
      .set('size', `${queryParams.pageSize}`)
      .set('sort', `${queryParams.sortField || 'order'},${queryParams.sortOrder || 'asc'}`);

    const filter = queryParams.filter || {};
    if (filter.search) params = params.set('search', filter.search);
    if (filter.nameContains) params = params.set('nameContains', filter.nameContains);
    if (filter.descriptionContains) {
      params = params.set('descriptionContains', filter.descriptionContains);
    }
    if (filter.orderContains) params = params.set('orderContains', filter.orderContains);
    if (filter.entityTypeEq) params = params.append('entityTypeEq', filter.entityTypeEq);

    return this.http
      .get<any>(API_URL, { headers: this.httpUtils.getHTTPHeaders(this.authToken), params })
      .pipe(
        map((res) => ({
          items: res?.data || [],
          totalCount: res?.count ?? 0,
        })),
      );
  }

  getServiceCatalogById(id: number): Observable<ServiceCatalogModel> {
    return this.http
      .get<any>(`${API_URL}/${id}`, { headers: this.httpUtils.getHTTPHeaders(this.authToken) })
      .pipe(map((res) => (res?.data?.id ? res.data : res)));
  }

  createServiceCatalog(serviceCatalog: ServiceCatalogModel): Observable<ServiceCatalogModel> {
    return this.http
      .post<any>(API_URL, ServiceCatalogService.toBody(serviceCatalog), {
        headers: this.httpUtils.getHTTPHeaders(this.authToken),
      })
      .pipe(map((res) => (res?.data?.id ? res.data : res)));
  }

  updateServiceCatalog(serviceCatalog: ServiceCatalogModel): Observable<ServiceCatalogModel> {
    return this.http
      .put<any>(`${API_URL}/${serviceCatalog.id}`, ServiceCatalogService.toBody(serviceCatalog), {
        headers: this.httpUtils.getHTTPHeaders(this.authToken),
      })
      .pipe(map((res) => (res?.data?.id ? res.data : res)));
  }

  deleteServiceCatalog(id: number): Observable<any> {
    return this.http.delete<any>(`${API_URL}/${id}`, {
      headers: this.httpUtils.getHTTPHeaders(this.authToken),
    });
  }

  listLiveServiceCatalogs(): Observable<ServiceCatalogModel[]> {
    const params = new HttpParams().set('sort', 'order,asc');
    return this.http
      .get<any>(API_URL, { headers: this.httpUtils.getHTTPHeaders(this.authToken), params })
      .pipe(
        map((res) => {
          const items = Array.isArray(res) ? res : res?.data || [];
          return items.slice().sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
        }),
      );
  }

  reorderServiceCatalogs(ids: number[]): Observable<ServiceCatalogModel[]> {
    return this.http
      .put<any>(
        `${API_URL}/order`,
        { ids },
        { headers: this.httpUtils.getHTTPHeaders(this.authToken) },
      )
      .pipe(map((res) => (Array.isArray(res) ? res : res?.data || [])));
  }

  private static toBody(serviceCatalog: ServiceCatalogModel): any {
    const entityTypes = [];
    (serviceCatalog.entityTypes || []).forEach((type) => {
      if (type && entityTypes.indexOf(type) === -1) entityTypes.push(type);
    });
    const taskTemplates = [];
    const seenTemplateIds = new Set<number>();
    (serviceCatalog.taskTemplates || []).forEach((template) => {
      if (!template?.id || seenTemplateIds.has(template.id)) return;
      seenTemplateIds.add(template.id);
      taskTemplates.push({ id: template.id, name: template.name });
    });
    return {
      name: serviceCatalog.name,
      description: serviceCatalog.description || null,
      entityTypes,
      taskTemplates,
    };
  }
}
